import { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Pagination, Typography, Input } from "antd";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { fetchProducts } from "../../../features/product/productSlice";
import ProductCard from "../Product-Card/ProductCard";
import GiftCardModal from "../Gift-Modal/GiftCardModal";
import "./AllProductPage.scss";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;
const { Search } = Input;

// Session Storage Keys
const SESSION_KEYS = {
  USER_COUNTRY: "user_selected_country",
  AVAILABLE_COUNTRIES: "available_countries_list",
};

const AllProductsPage = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, totalCount, currentPage } = useSelector(
    (state) => state.product
  );

  const [open, setOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce effect - 500ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    // Reset to page 1 when search changes
    if (searchQuery !== debouncedSearch) {
      // We'll handle page reset in the fetchProducts call
    }

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery, debouncedSearch]);

  // Helper functions for session storage
  const getStoredCountry = useCallback(() => {
    return sessionStorage.getItem(SESSION_KEYS.USER_COUNTRY);
  }, []);

  // Fetch products when currentPage, language, or debounced search changes
  useEffect(() => {
    dispatch(
      fetchProducts({
        page: currentPage,
        limit: 20,
        categoryId: "",
        categoryName: getStoredCountry(),
        userId: "",
        country: "",
        search: debouncedSearch,
      })
    );
  }, [dispatch, currentPage, i18next.language, debouncedSearch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Memoized modal handlers
  const handleAddToCart = useCallback((product) => {
    setOpen(true);
    setSelectedCard(product);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectProduct = useCallback(
    (product) => {
      navigate("/product-details", { state: { product } });
    },
    [navigate]
  );

  // Memoized page change handler
  const handlePageChange = useCallback(
    (newPage) => {
      dispatch(fetchProducts({ page: newPage, search: debouncedSearch }));
    },
    [dispatch, debouncedSearch]
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle search with Enter key
  const handleSearch = useCallback((value) => {
    setDebouncedSearch(value);
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setDebouncedSearch("");
  }, []);

  // Memoized language-dependent text alignment
  const textAlignment = useMemo(
    () => ({
      textAlign: i18next.language === "ar" ? "right" : "left",
      display: "flex",
      justifyContent: "space-between",
    }),
    [i18next.language]
  );

  // Memoized product card click handler
  const createProductCardClickHandler = useCallback(
    (product) => () => {
      handleSelectProduct(product);
    },
    [handleSelectProduct]
  );

  // Memoized product list to prevent unnecessary re-renders
  const productList = useMemo(
    () => (
      <Row gutter={[16, 16]}>
        {products.map((product) => (
          <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
            <ProductCard
              product={product}
              onAddToCart={handleAddToCart}
              handleSelectProduct={createProductCardClickHandler(product)}
            />
          </Col>
        ))}
      </Row>
    ),
    [products, handleAddToCart, createProductCardClickHandler]
  );

  // Memoized pagination component
  const paginationComponent = useMemo(
    () => (
      <div
        className="pagination-container"
        style={{ textAlign: "center", marginTop: 24 }}
      >
        <Pagination
          current={currentPage}
          total={totalCount}
          pageSize={20}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </div>
    ),
    [currentPage, totalCount, handlePageChange]
  );

  return (
    <section className="all-products-section">
      <div className="container">
        <div className="header" style={textAlignment}>
          <Title className="title" level={2}>
            {t("store_header.all_products")}
          </Title>

          <div
            className="search-container"
            style={{
              marginBottom: 24,
              display: "flex",
              justifyContent:
                i18next.language === "ar" ? "flex-end" : "flex-start",
            }}
          >
            <Search
              className="green-search"
              placeholder={t("search_placeholder", "Search products...")}
              allowClear
              value={searchQuery}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              style={{ width: 300 }}
              enterButton
            />
          </div>
        </div>

        {/* Search Input Field */}

        <>
          {productList}
          {paginationComponent}
        </>

        <GiftCardModal
          visible={open}
          onClose={handleCloseModal}
          product={selectedCard}
        />
      </div>
    </section>
  );
};

export default AllProductsPage;
